/**
 * MAHI — the wardrobe entry point, top-right of Discover.
 *
 * Deliberately the same 36px circle as the profile avatar at top-left: the two
 * frame the screen, so they read as a matched pair rather than two unrelated
 * widgets. The avatar is neutral (white, slate ring); this one carries the
 * wardrobe mark itself, because it's a destination rather than an identity.
 *
 * The mark is shared with the save button in the action row below and with
 * the profile panel's Wardrobe row — see WardrobeMark.
 *
 * The count badge pulses when it increments — paired with the card flying into
 * this button on a right-swipe (see DiscoveryFeed), it teaches where saved
 * things go without a word of instruction.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { WardrobeMark } from '../ui/marks';

const WardrobeButton = React.forwardRef(({ count = 0, onClick }, ref) => {
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(false);
  const previous = useRef(count);

  useEffect(() => {
    if (count > previous.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 620);
      previous.current = count;
      return () => clearTimeout(t);
    }
    previous.current = count;
  }, [count]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick || (() => navigate('/wardrobe'))}
      data-testid="wardrobe-button"
      data-tour="wardrobe"
      aria-label={count > 0 ? `Wardrobe, ${count} saved` : 'Wardrobe'}
      className="relative shrink-0 w-9 h-9 rounded-full bg-white ring-1 ring-raspberry-200 flex items-center justify-center text-raspberry-600 active:scale-95 transition-transform"
    >
      <motion.span
        className="absolute inset-0 rounded-full bg-raspberry-300"
        initial={false}
        animate={pulse ? { opacity: [0, 0.5, 0], scale: [1, 1.5, 1.6] } : { opacity: 0, scale: 1 }}
        transition={{ duration: 0.62, ease: 'easeOut' }}
      />
      {/* Full-bleed: the artwork is already a circle, so it *is* the
          button face rather than a glyph sitting inside one. */}
      <WardrobeMark className="relative w-full h-full" />

      <AnimatePresence>
        {count > 0 && (
          <motion.span
            key="badge"
            data-testid="wardrobe-count"
            // The badge springs on each increment — a number quietly changing
            // is easy to miss on a phone; the movement is not.
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: pulse ? [1, 1.35, 1] : 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 520, damping: 18 }}
            className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-raspberry-500 text-white text-[10px] font-semibold font-mahi-sans flex items-center justify-center ring-2 ring-white tabular-nums"
          >
            {count > 99 ? '99+' : count}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
});

WardrobeButton.displayName = 'WardrobeButton';
export default WardrobeButton;
